<section class="flex flex-col gap-5 rounded-2xl border border-border bg-card p-6 shadow-sm">
  <header class="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-3">
    <div class="flex min-w-0 flex-col gap-1">
      <p class="cmt-eyebrow">Number Blocks</p>
      <p class="text-sm text-muted-foreground">
        Number blocks linked to this brand.
      </p>
    </div>
    <button
      hlmBtn
      variant="default"
      size="sm"
      type="button"
      (click)="openAttach()"
      [disabled]="!canWrite()"
      [attr.title]="!canWrite() ? 'Read-only access — needs amie.brandManager.write to enable' : null"
    >
      <hlm-icon name="lucidePlus" size="0.9rem" /> Link Number Blocks
    </button>
  </header>

  @if (loading()) {
    <div class="flex items-center gap-3 py-4 text-sm text-muted-foreground">
      <hlm-spinner size="20" /> Loading number blocks…
    </div>
  } @else if (blocks().length === 0) {
    <div class="rounded-xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
      No number blocks linked to this brand yet. Click
      <span class="font-medium text-foreground">Link Number Blocks</span> to connect one.
    </div>
  } @else {
    <!-- View controls -->
    <div class="flex flex-wrap items-center gap-3">
      <div class="relative max-w-md flex-1 min-w-[240px]">
        <hlm-icon
          name="lucideSearch"
          size="1rem"
          class="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          hlmInput
          size="sm"
          class="pl-11 pr-10"
          placeholder="Search by area code, prefix, carrier, location, or country…"
          [value]="filterText()"
          (input)="filterText.set($any($event.target).value)"
        />
        @if (filterText()) {
          <button
            hlmBtn
            variant="ghost"
            size="icon"
            class="absolute right-1 top-1/2 h-11 w-11 md:h-7 md:w-7 -translate-y-1/2"
            type="button"
            tabindex="-1"
            (click)="filterText.set('')"
            aria-label="Clear search"
          >
            <hlm-icon name="lucideX" size="0.8rem" />
          </button>
        }
      </div>

      <div class="inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1.5 font-mono-feat text-xs uppercase tracking-[0.1em] text-muted-foreground">
        @if (filterText()) {
          <span>{{ visibleCount() }}</span>
          <span class="opacity-50">of</span>
          <span>{{ count() }} blocks</span>
        } @else {
          <span class="text-foreground">{{ count() }}</span>
          <span>blocks linked</span>
        }
      </div>

      <!-- Surfaces only when at least one column filter is active. -->
      @if (hasColumnFilter()) {
        <button
          hlmBtn
          variant="outline"
          size="sm"
          type="button"
          (click)="clearColumnFilters()"
        >
          <hlm-icon name="lucideFilter" size="0.9rem" /> Clear filters
        </button>
      }

      <!-- Inline detach toolbar — surfaces only when rows are selected. Inline
           (not a fixed/floating page pill) because the panel is embedded in the
           brand form; a viewport-pinned bar would float over unrelated chrome. -->
      @if (selectedCount() > 0) {
        <div class="ml-auto inline-flex items-center gap-3 rounded-full border border-border bg-muted/40 py-1.5 pl-4 pr-2">
          <span class="text-sm text-muted-foreground">
            <span class="font-semibold text-foreground">{{ selectedCount() }}</span>
            {{ selectedCount() === 1 ? 'block' : 'blocks' }} selected
          </span>
          <button
            hlmBtn
            variant="destructive"
            size="sm"
            type="button"
            class="rounded-full"
            (click)="confirmDetach()"
            [disabled]="!canWrite()"
            [attr.title]="!canWrite() ? 'Read-only access — needs amie.brandManager.write to enable' : null"
          >
            <hlm-icon name="lucideX" size="0.9rem" />
            Unlink {{ selectedCount() }} {{ selectedCount() === 1 ? 'block' : 'blocks' }}
          </button>
        </div>
      }
    </div>

    @if (filteredBlocks().length === 0) {
      <div class="rounded-xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
        No blocks match "<span class="font-mono-feat">{{ filterText() }}</span>".
      </div>
    } @else {
      <!-- ≥1024px: AG-Grid. Definite-height wrapper + normal layout so the grid
           scrolls internally and pins the column header (native behaviour). -->
      <div class="hidden h-[28rem] lg:block">
        <ag-grid-angular
          class="ag-theme-material cmt-brand-grid h-full"
          [rowData]="filteredBlocks()"
          [columnDefs]="columnDefs"
          [defaultColDef]="defaultColDef"
          [rowSelection]="'multiple'"
          [suppressRowClickSelection]="true"
          [suppressHorizontalScroll]="true"
          [getRowId]="getRowId"
          (gridReady)="onGridReady($event)"
          (firstDataRendered)="onFirstDataRendered($event)"
          (gridSizeChanged)="onGridSizeChanged($event)"
          (selectionChanged)="onSelectionChanged($event)"
          (filterChanged)="onFilterChanged($event)"
        />
      </div>

      <!-- <1024px: stacked cards. Tap a card to toggle selection — same inline
           unlink toolbar above applies. -->
      <div class="flex max-h-[28rem] flex-col gap-2.5 overflow-y-auto pr-1 lg:hidden">
        <!-- Master select-all row — mirrors the grid's header checkbox.
             Filtered-only semantics so a search-narrow + select-all doesn't
             sweep up the entire dataset. -->
        <button
          type="button"
          role="checkbox"
          [attr.aria-checked]="mobileSelectionState() === 'all' ? 'true' : mobileSelectionState() === 'partial' ? 'mixed' : 'false'"
          class="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-border bg-muted/40 p-3 text-left transition hover:border-primary/40 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          (click)="toggleSelectAllMobile()"
        >
          <span
            class="flex h-5 w-5 flex-none items-center justify-center rounded border transition"
            [class.bg-primary]="mobileSelectionState() !== 'none'"
            [class.border-primary]="mobileSelectionState() !== 'none'"
            [class.border-border]="mobileSelectionState() === 'none'"
            aria-hidden="true"
          >
            @if (mobileSelectionState() === 'all') {
              <hlm-icon name="lucideCheck" size="0.8rem" class="text-primary-foreground" />
            } @else if (mobileSelectionState() === 'partial') {
              <hlm-icon name="lucideMinus" size="0.8rem" class="text-primary-foreground" />
            }
          </span>
          <span class="flex-1 text-sm font-medium text-foreground">
            @if (mobileSelectionState() === 'all') {
              @if (filterText()) { Deselect all matches } @else { Deselect all {{ count() }} }
            } @else {
              @if (filterText()) { Select all matches } @else { Select all {{ count() }} }
            }
          </span>
          @if (filterText()) {
            <span class="font-mono-feat text-xs text-muted-foreground">
              {{ visibleCount() }} of {{ count() }}
            </span>
          }
        </button>

        @for (block of mobileVisibleBlocks(); track block.key) {
          <button
            type="button"
            role="checkbox"
            [attr.aria-checked]="isCardSelected(block)"
            class="group relative flex w-full cursor-pointer items-center gap-3 rounded-xl border bg-card p-3 text-left shadow-sm transition hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            [class.border-primary]="isCardSelected(block)"
            [class.bg-accent]="isCardSelected(block)"
            [class.border-border]="!isCardSelected(block)"
            (click)="toggleCardSelect(block)"
          >
            <span
              class="flex h-5 w-5 flex-none items-center justify-center rounded border transition"
              [class.bg-primary]="isCardSelected(block)"
              [class.border-primary]="isCardSelected(block)"
              [class.border-border]="!isCardSelected(block)"
              aria-hidden="true"
            >
              @if (isCardSelected(block)) {
                <hlm-icon name="lucideCheck" size="0.8rem" class="text-primary-foreground" />
              }
            </span>

            @if (isoForBlock(block); as iso) {
              <cmt-flag [iso]="iso" width="1.1rem" height="0.78rem" class="flex-none" />
            }

            <div class="min-w-0 flex-1">
              <div class="flex items-center justify-between gap-2">
                <span class="truncate text-sm font-semibold text-foreground">
                  {{ block.staLand || '—' }}
                </span>
                <span class="flex-none font-mono-feat text-xs text-muted-foreground">
                  {{ block.vorwahl }} · {{ block.prefix }}
                </span>
              </div>
              <div class="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                <span class="truncate">{{ block.staOrt || '—' }}</span>
                <span class="opacity-40" aria-hidden="true">·</span>
                <span class="truncate">{{ block.carrier || '—' }}</span>
              </div>
            </div>
          </button>
        }

        @if (mobileHasMore()) {
          <button
            hlmBtn
            variant="outline"
            size="sm"
            type="button"
            class="mt-1 self-center"
            (click)="loadMoreMobile()"
          >
            Show 100 more
            <span class="ml-1 font-mono-feat text-xs text-muted-foreground">
              ({{ mobileVisibleBlocks().length }} / {{ filteredBlocks().length }})
            </span>
          </button>
        }
      </div>
    }
  }
</section>
